import { useCallback, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { toaster, logger } from '@axon/ui';

import { useRouter } from '@/i18n/navigation';
import { useAuthStore } from '@/stores/auth.store';
import type { LoginCredentials, RegisterCredentials } from '@/types/auth';
import { authService } from '@/services/auth.service';

export function useAuth() {
  const router = useRouter();
  const store = useAuthStore();
  const queryClient = useQueryClient();
  // B-179: login success toast was hardcoded English ("Welcome back!",
  // "Logged in as ..."). Pull locale-aware copy from the `auth` namespace.
  const t = useTranslations('auth');

  // Check auth status on mount - only if we have a token
  useEffect(() => {
    // Only check if we have a token but no user data, and not currently loading
    const hasToken = store.token || document.cookie.includes('access-token');

    if (hasToken && !store.user && !store.isLoading && !store.hasCheckedAuth) {
      const checkAuth = async () => {
        try {
          store.setLoading(true);
          store.setHasCheckedAuth(true); // Prevent multiple checks

          const response = await authService.verify();

          if (response.user) {
            store.setUser(response.user);
          }
        } catch {
          // Not authenticated, that's okay - don't spam console
          // Clear any stored token if auth fails
          store.logout();
        } finally {
          store.setLoading(false);
        }
      };

      checkAuth();
    } else if (!hasToken && !store.hasCheckedAuth) {
      // No token, no need to check - just mark as checked
      store.setHasCheckedAuth(true);
      store.setLoading(false);
    }
  }, [store]);

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      try {
        store.setLoading(true);

        const response = await authService.login(credentials);

        // B-223: drop any cache left over from a previous session before the
        // new user's data loads — covers the case where the app reached the
        // login screen without an explicit logout (e.g. expired session).
        queryClient.clear();

        // A fresh, explicit login is never a preview/impersonation — clear any
        // stale impersonation state so the demo "preview" banner doesn't stick.
        store.clearImpersonation();

        // Update store with user data and tokens
        store.setUser(response.user);

        // Set tokens in store
        if (response.accessToken) {
          logger.info('Storing tokens in auth store', {
            hasAccessToken: !!response.accessToken,
            hasRefreshToken: !!response.refreshToken,
            expiresIn: response.expiresIn,
          });

          store.setTokens(
            response.accessToken,
            response.refreshToken || '', // Backend sends refreshToken in AuthResponse
            response.expiresIn
          );
        }

        // Show success message
        toaster.create({
          title: t('welcomeBack'),
          description: t('loggedInAs', { email: response.user.email }),
          type: 'success',
        });

        // Determine redirect path:
        // 1. Check if onboarding is complete - redirect to /onboarding if not
        // 2. Use custom redirectPath if provided (validated against whitelist)
        // 3. Otherwise, use role-based routing
        // 4. Default to /dashboard
        let redirectPath = credentials.redirectPath;
        let isExternalRedirect = false;

        // Validate external URLs against *.axonfleet.com whitelist (open redirect protection)
        if (redirectPath && /^https?:\/\//.test(redirectPath)) {
          try {
            const url = new URL(redirectPath);
            if (url.hostname === 'axonfleet.com' || url.hostname.endsWith('.axonfleet.com')) {
              isExternalRedirect = true;
            } else {
              // Reject non-axonfleet.com URLs — fall through to default routing
              redirectPath = undefined;
            }
          } catch {
            redirectPath = undefined;
          }
        }

        if (!redirectPath) {
          // Check if user needs onboarding (fetch status from API).
          //
          // Issue #168 — a fresh tenant has no `onboarding_status` row
          // until the backend's TenantsService.create seeds one. Both
          // fixes are now in place, but defense-in-depth: treat 404 as
          // "onboarding not yet started" → redirect to `/onboarding`.
          // Anything else (network error, 5xx) still falls through to
          // the dashboard so a transient API hiccup doesn't trap the
          // user in the setup wizard.
          try {
            const res = await fetch('/api/v1/onboarding/status', {
              credentials: 'include',
            });

            const isDriver = response.user.roles?.some((r) => r.toUpperCase() === 'DRIVER');

            if (res.ok) {
              const onboardingStatus = await res.json();
              redirectPath =
                onboardingStatus && !onboardingStatus.isComplete
                  ? '/onboarding'
                  : isDriver
                    ? '/driver'
                    : '/dashboard';
            } else if (res.status === 404) {
              // Onboarding row not seeded yet — first login of a
              // freshly-provisioned tenant (or a backend that lost the
              // row). Land on the wizard, not the dashboard.
              redirectPath = '/onboarding';
            } else {
              redirectPath = isDriver ? '/driver' : '/dashboard';
            }
          } catch {
            // Network / parse failure — default to dashboard so a
            // transient hiccup doesn't strand the user mid-flow.
            const isDriver = response.user.roles?.some((r) => r.toUpperCase() === 'DRIVER');
            redirectPath = isDriver ? '/driver' : '/dashboard';
          }
        }

        if (isExternalRedirect) {
          window.location.href = redirectPath!;
        } else {
          router.push(redirectPath!);
        }
      } catch (err: unknown) {
        const error = err as Error;
        const message = error.message || 'Login failed';

        toaster.create({
          title: t('loginError'),
          description: message,
          type: 'error',
        });

        throw err;
      } finally {
        store.setLoading(false);
      }
    },
    [store, router, t, queryClient]
  );

  const logout = useCallback(async () => {
    try {
      await authService.logout();
      store.logout();
      // B-223: drop the entire React Query cache on logout. Without this the
      // next user who logs in on the same tab inherits the previous user's
      // cached queries (dashboard, profile, lists), so the panel rendered
      // stale data and errored ("veriler sıfırlanmıyor ve panel hatası").
      queryClient.clear();

      // B-222: these were hardcoded English, so the logout toast showed in
      // English on /tr. use-auth already has `t = useTranslations('auth')`.
      toaster.create({
        title: t('logoutSuccess'),
        description: t('logoutSuccessDescription'),
        type: 'info',
      });

      router.push('/login');
    } catch (error) {
      logger.error('Logout failed:', error);

      // Even if logout fails, clear local state and redirect
      store.logout();
      queryClient.clear();
      router.push('/login');
    }
  }, [store, router, t, queryClient]);

  const register = useCallback(
    async (credentials: RegisterCredentials) => {
      try {
        store.setLoading(true);

        const response = await authService.register(credentials);

        // Update store with user data
        // Tokens are stored in httpOnly cookies, so we don't have access to them
        store.setUser(response.user);

        // Set token expiry if provided
        if (response.expiresIn) {
          store.setTokens('', '', response.expiresIn);
        }

        toaster.create({
          title: t('registerSuccess'),
          description: t('registerSuccessMessage'),
          type: 'success',
        });

        // Redirect to dashboard or onboarding
        router.push('/dashboard');
      } catch (err: unknown) {
        const error = err as Error;
        const message = error.message || 'Registration failed';

        toaster.create({
          title: t('registerError'),
          description: message,
          type: 'error',
        });

        throw err;
      } finally {
        store.setLoading(false);
      }
    },
    [store, router, t]
  );

  const refreshSession = useCallback(async () => {
    try {
      const response = await authService.refreshToken();

      // Update token expiry
      if (response.expiresIn) {
        store.setTokens('', '', response.expiresIn);
      }

      // Update user if returned
      if (response.user) {
        store.setUser(response.user);
      }
    } catch (error) {
      logger.error('Session refresh failed:', error);
      throw error;
    }
  }, [store]);

  const checkSessionExpiry = useCallback(() => {
    const expiry = store.tokenExpiresAt;

    // Defensive checks for invalid expiry values
    if (!expiry || isNaN(expiry) || typeof expiry !== 'number') {
      logger.error('Invalid token expiry:', expiry);
      return false;
    }

    const now = Date.now();
    const timeUntilExpiry = expiry - now;

    // If less than 5 minutes until expiry, refresh
    if (timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
      refreshSession().catch((error) => logger.error('Session refresh failed:', error));
    }

    return timeUntilExpiry > 0;
  }, [store.tokenExpiresAt, refreshSession]);

  // Set up session expiry check
  useEffect(() => {
    if (!store.isAuthenticated) return;

    const interval = setInterval(() => {
      const isValid = checkSessionExpiry();
      if (!isValid) {
        logout();
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [store.isAuthenticated, checkSessionExpiry, logout]);

  return {
    // State
    user: store.user,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
    tenantId: store.tenantId,

    // Actions
    login,
    logout,
    register,
    refreshSession,

    // Permission helpers
    hasPermission: store.hasPermission,
    hasRole: store.hasRole,
    hasAnyRole: store.hasAnyRole,
    canAccess: (resource: string, action: string) => {
      const permission = `${resource}:${action}`;
      return store.hasPermission(permission);
    },
  };
}
